<script lang="ts">
  /**
   * SwimHeroCard — calm Oura-style hero (hero redesign).
   * Flat canvas layout: date → big distance number → story headline →
   * muted subline → swim-shape strip → peak HR tile.
   *
   * Three branches: loading → skeleton; ready with positive distance → card;
   * everything else → SwimHeroGreeting fallback.
   */
  import SwimHeroGreeting from "./SwimHeroGreeting.svelte";
  import {
    currentRawFirehose,
    currentFirehoseStatus,
    currentDerivedWorkout,
  } from "../lib/state";
  import {
    formatHeroDay,
    fmtClockDuration,
    fmtPace,
    pacePer100mSeconds,
  } from "../lib/format";
  import { heroStory } from "../lib/setStory";
  import { showcase } from "../lib/nav";
  import { strokeColor } from "../lib/designTokens";
  import type { Lap } from "../lib/laps";
  import { peakHrContext } from "../lib/peakHrContext";
  import { openWaterTemp, formatWaterTemp } from "../lib/openWaterHeader";

  let w = $derived($currentRawFirehose?.workout ?? null);
  let distanceValue = $derived(
    w?.totalDistance &&
      Number.isFinite(w.totalDistance.value) &&
      w.totalDistance.value > 0
      ? w.totalDistance.value
      : null,
  );
  let paceSec = $derived(
    w ? pacePer100mSeconds(w.durationSeconds, w.totalDistance ?? null) : null,
  );
  // The hero story is a pool/set fact-ladder line ("your fastest set", "steady
  // from the first length to the last") — pool-shaped by construction. Open
  // water has no sets or lengths, so suppress it: the "Open water" marker below
  // carries the identity, and no canned set-story can leak pool language.
  let story = $derived(
    $currentDerivedWorkout && !$currentDerivedWorkout.isOpenWater
      ? heroStory($currentDerivedWorkout.setGroups, $showcase)
      : null,
  );
  // Built in script, not the template — Svelte collapses whitespace around
  // {#if} boundaries (lesson captured in SetRibbonSection).
  let subline = $derived(
    w
      ? `${fmtClockDuration(w.durationSeconds)} in the water${
          paceSec !== null ? ` · ${fmtPace(paceSec)} per 100m` : ""
        }`
      : "",
  );

  // `dw`, not `derived` — a local named `derived` collides with the $derived
  // rune and trips svelte-check ("$derived used before its declaration").
  let dw = $derived($currentDerivedWorkout ?? null);

  // Open-water identity: a calm marker (not a shouty badge) plus water temp when
  // the swim actually carries one. The founding fixture has no temp, so the
  // verifiable path today is marker-only (formatWaterTemp returns null → omit).
  let isOpenWater = $derived(dw?.isOpenWater === true);
  let waterTempLabel = $derived(
    isOpenWater && $currentRawFirehose
      ? formatWaterTemp(openWaterTemp($currentRawFirehose))
      : null,
  );

  let peakBpm = $derived(dw?.maxHr !== null && dw?.maxHr !== undefined
    ? Math.round(dw.maxHr)
    : null);

  let peakCtx = $derived(
    dw
      ? peakHrContext(dw.peakHrTs, dw.setGroups, dw.laps)
      : { setLabel: null, stroke: null }
  );

  // Warmup/Cooldown are POSITION guesses (first set / last set), not verified
  // owner intent — naming them directly ("during your Cooldown") asserts
  // intent the app can't confirm. Neutral position-fact phrasing for these
  // two labels only; every other label still reads "your {label}" (steering-
  // panel fix, 2026-07-05) — EXCEPT labels that are already a full phrase
  // starting with "The " ("The whole swim", "The Rockalita"): prefixing
  // "your" there doubles the article ("your The whole swim"). Those read
  // as-is: "during The whole swim" (persona-gate fix, W1 fix round).
  let peakSetPhrase = $derived(
    peakCtx.setLabel === "Warmup"
      ? "your first set"
      : peakCtx.setLabel === "Cooldown"
        ? "your last set"
        : peakCtx.setLabel === "The whole swim"
          ? // Single-set swims (open water, or a pool swim with no set breaks):
            // "peak ... during the whole swim" self-contradicts — a peak is an
            // instant, "the whole swim" is a duration (persona gate 2026-07-11,
            // B4, 5/5 panelists). The set phrase carries no information when
            // there is only one set, so it drops and the stroke stands alone.
            null
          : peakCtx.setLabel
            ? peakCtx.setLabel.startsWith("The ")
              ? peakCtx.setLabel
              : `your ${peakCtx.setLabel}`
            : null
  );

  let peakContextLine = $derived(
    peakBpm !== null
      ? peakSetPhrase && peakCtx.stroke
        ? `bpm during ${peakSetPhrase}, on ${peakCtx.stroke}`
        : peakSetPhrase
          ? `bpm during ${peakSetPhrase}`
          : peakCtx.stroke
            ? `bpm, on ${peakCtx.stroke}`
            : `bpm`
      : null
  );

  // CONTINUOUS flow strip: the whole swim's lengths in order, with consecutive
  // same-stroke lengths MERGED into one band (no stripes, no set gaps). The
  // color changes only where the stroke changed, so it reads as one calm flow
  // of your true strokes start to finish. (Scott's design.)
  function strokeRuns(laps: Lap[]): { stroke: string; count: number }[] {
    const runs: { stroke: string; count: number }[] = [];
    for (const l of laps) {
      const last = runs[runs.length - 1];
      if (last && last.stroke === l.strokeStyle) last.count++;
      else runs.push({ stroke: l.strokeStyle, count: 1 });
    }
    return runs;
  }
</script>

{#if $currentFirehoseStatus === "loading"}
  <div class="hero-card skeleton" aria-hidden="true">
    <div class="bar bar-date"></div>
    <div class="bar bar-number"></div>
    <div class="bar bar-subline"></div>
    <div class="bar bar-story"></div>
  </div>
{:else if $currentFirehoseStatus === "ready" && w && distanceValue !== null}
  <div class="hero-card">
    {#if $showcase}
    <p class="date-line">{formatHeroDay(w.startDate)}</p>
    {/if}
    {#if isOpenWater}
      <p class="ow-marker">
        <span class="ow-dot" aria-hidden="true"></span>
        <span class="ow-text">Open water</span>
        {#if waterTempLabel}<span class="ow-temp">· {waterTempLabel}</span>{/if}
      </p>
    {/if}
    <div class="number-row">
      <span class="digits">{Math.round(distanceValue).toLocaleString()}</span>
      <span class="unit">metres</span>
    </div>
    {#if story}<p class="story">{story}</p>{/if}
    <p class="subline">{subline}</p>

    {#if dw?.setGroups && dw.setGroups.length > 0}
      {@const allLaps = dw.setGroups.flatMap((sg) => sg.laps)}
      {@const runs = strokeRuns(allLaps)}
      {@const uniqueStrokes = [...new Map(runs.map((r) => [r.stroke, r])).values()]}
      <div class="shape-wrap">
        <p class="shape-caption">How you swam, start to finish</p>
        <div class="shape-strip fl-strip">
          {#each runs as run, ri (ri)}
            <div class="fl-seg" style="flex-grow: {run.count}; background: {strokeColor(run.stroke)}"></div>
          {/each}
        </div>
        <div class="stroke-key">
          {#each uniqueStrokes as s (s.stroke)}
            <span class="key-item">
              <span class="key-dot" style="background: {strokeColor(s.stroke)}"></span>
              <span class="key-name">{s.stroke}</span>
            </span>
          {/each}
        </div>
      </div>
    {/if}

    {#if peakBpm !== null}
      <div class="peak-tile">
        <p class="peak-label">PEAK HEART RATE</p>
        <p class="peak-bpm">{peakBpm}</p>
        {#if peakContextLine}<p class="peak-context">{peakContextLine}</p>{/if}
      </div>
    {/if}
  </div>
{:else}
  <SwimHeroGreeting />
{/if}

<style>
  .hero-card {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .date-line {
    /* Label tier (12px uppercase) — quiet metadata above the hero number, a
       clear step below the body text so the big number leads unchallenged. */
    font-size: 12px;
    line-height: 1.33;
    /* Brighter than --text-muted (#8F9098) so it stays legible over the
       sunrise-glow background for low vision, while still reading as metadata. */
    color: #ABACB4;
    margin: 0 0 var(--space-2);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  /* Open-water marker — a calm identity line above the hero number, not a
     shouty badge. Sky-blue dot (water role) + muted label, sitting at the
     same quiet tier as the date line so it reads as metadata, not a banner. */
  .ow-marker {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0 0 var(--space-2);
    font-size: 15px;
    line-height: 1.3;
  }
  .ow-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--accent-secondary);
  }
  .ow-text {
    color: #C5C6CC;
    font-weight: 600;
  }
  .ow-temp {
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }

  .number-row {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    margin: 0 0 var(--space-2);
  }

  .digits {
    font: 700 76px/1 var(--font-display);
    color: #E4E2E4;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.03em;
  }

  .unit {
    font-size: 18px;
    color: var(--text-muted);
  }

  .story {
    font: 600 22px/1.35 var(--font-text, inherit);
    color: #E4E2E4;
    margin: 0 0 var(--space-1);
  }

  .subline {
    /* Time in water + pace is data Scott reads, not decoration — brighter than
       --text-muted so it is legible at a glance. */
    font-size: 15px;
    line-height: 1.3;
    color: #C5C6CC;
    margin: 0 0 var(--space-5);
  }

  .shape-wrap {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: 0 0 var(--space-5);
  }

  .shape-strip {
    display: flex;
    height: 14px;
    border-radius: 8px;
    overflow: hidden;
    gap: 2px;
  }

  /* Label ABOVE the strip, read like a small chart title. */
  .shape-caption {
    font-size: 12px;
    color: #ABACB4;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  /* Continuous flow: same-stroke lengths merged into one band, no gaps,
     no stripes. The color changes only where the stroke changed. */
  .fl-strip {
    gap: 0;
    height: 16px;
  }
  .fl-seg {
    height: 100%;
  }

  /* Stroke color key — compact row of dot + name for each unique stroke used. */
  .stroke-key {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 4px;
  }
  .key-item {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .key-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .key-name {
    font-size: 15px;
    color: #ABACB4;
    line-height: 1;
  }

  /* No hard outline — the soft translucent backing is enough to hold the
     numbers legible over the gradient. Dropping the 1px border is what stops
     the screen reading as a stack of outlined widgets (the Oura-calm move). */
  .peak-tile {
    background: var(--bg-card);
    border-radius: 14px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .peak-label {
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #ABACB4;
    margin: 0;
  }

  .peak-bpm {
    font: 700 30px/1.1 var(--font-display);
    color: #FF5722;
    margin: 0;
  }

  .peak-context {
    font-size: 14px;
    line-height: 1.35;
    color: #C5C6CC;
    margin: 0;
  }

  /* Loading skeleton — bars approximate the date / big number / subline / story
     so nothing below shifts on fill. */
  .skeleton {
    justify-content: center;
  }
  .bar {
    background: var(--bg-raised, rgba(255, 255, 255, 0.08));
    border-radius: 6px;
  }
  .bar-date {
    width: 40%;
    height: 14px;
  }
  .bar-number {
    width: 65%;
    height: 46px;
    margin: 6px 0;
  }
  .bar-subline {
    width: 55%;
    height: 14px;
  }
  .bar-story {
    width: 70%;
    height: 16px;
    margin-top: 6px;
  }
  @media (prefers-reduced-motion: no-preference) {
    .skeleton .bar {
      animation: hero-pulse 1.4s ease-in-out infinite;
    }
    @keyframes hero-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.45;
      }
    }
  }
</style>
